import { useCallback, useEffect, useState } from "react";
import { ClipboardCheck, Download, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { fmtDateTime } from "@/lib/checkins";
import { StatusCard } from "../StatusCard";
import { AlertRow } from "../staff/AlertRow";
import { SubmissionDialog } from "../staff/SubmissionDialog";
import { errMsg } from "../staff/ResolveDialog";
import { EnrolDialog } from "./EnrolDialog";
import { EnrolmentActionDialog } from "./EnrolmentActionDialog";
import { EnrolmentCard } from "./EnrolmentCard";
import { DiaryList } from "./DiaryList";
import { TrendGrid } from "./TrendGrid";

const H = ({ children }) => <h3 className="font-display text-base font-semibold text-zinc-950">{children}</h3>;

export function ClientCheckinsTab({ email, clientName, canonicalId }) {
  const { user } = useAuth();
  const readOnly = user?.role === "auditor";
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState({ templates: [], staff: [], wording: "" });
  const [enrolOpen, setEnrolOpen] = useState(false);
  const [action, setAction] = useState(null);
  const [openSid, setOpenSid] = useState(null);

  const load = useCallback(() => {
    api.get(`/clients/${encodeURIComponent(email)}/checkin-summary`, { params: { canonical_client_id: canonicalId || undefined } })
      .then((r) => setData(r.data)).catch((e) => toast.error("Couldn't load progress reviews", { description: errMsg(e) }));
  }, [email, canonicalId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    Promise.all([api.get("/checkin-templates"), api.get("/checkin-staff"), api.get("/checkin-settings")])
      .then(([t, s, c]) => setMeta({ templates: t.data, staff: s.data, wording: c.data.consent_wording })).catch(() => {});
  }, []);

  if (!data) return <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />;
  const latest = data.submissions[data.submissions.length - 1];
  const canEnrol = !readOnly && !data.enrolments.some((e) => ["active", "paused"].includes(e.status));
  const replaceAlert = (a) => setData((d) => ({ ...d, alerts: d.alerts.map((x) => (x.id === a.id ? a : x)) }));

  return (
    <div className="space-y-5" data-testid="client-checkins-tab">
      <div className="flex flex-wrap items-center gap-2">
        <ClipboardCheck className="h-5 w-5 text-zinc-500" />
        <h2 className="font-display text-lg font-semibold text-zinc-950">Progress Review</h2>
        <div className="ml-auto flex gap-2">
          {data.submissions.length > 0 && !readOnly && (
            <Button size="sm" variant="ghost" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${encodeURIComponent(email)}/checkin-export`, "_blank")} data-testid="checkin-export-btn">
              <Download className="mr-1.5 h-3.5 w-3.5" />Export
            </Button>
          )}
          {canEnrol && <Button size="sm" onClick={() => setEnrolOpen(true)} data-testid="checkin-enrol-btn"><Plus className="mr-1.5 h-3.5 w-3.5" />Enrol in progress review</Button>}
        </div>
      </div>
      {!data.enrolments.length && (
        <Card className="p-6 text-sm text-zinc-600" data-testid="checkin-empty">
          Not enrolled. Enrol this client to email a short exercise progress review every 4 or 6 weeks — they get a traffic-light summary, you get trends and safety alerts.
        </Card>
      )}
      {data.enrolments.map((e) => <EnrolmentCard key={e.id} enrolment={e} readOnly={readOnly} onChanged={load} onAction={(en, k) => setAction({ en, k })} />)}
      {data.diaries?.length > 0 && <DiaryList rows={data.diaries} onOpen={setOpenSid} />}
      {data.alerts.length > 0 && (
        <div className="space-y-2" data-testid="checkin-client-alerts">
          <H>Open alerts & tasks</H>
          {data.alerts.map((a) => <AlertRow key={a.id} alert={a} staff={meta.staff} readOnly={readOnly} showClient={false} onChanged={replaceAlert} onOpenSubmission={setOpenSid} />)}
        </div>
      )}
      {latest && (
        <div className="space-y-2" data-testid="checkin-latest">
          <div className="flex flex-wrap items-baseline gap-2">
            <H>Latest progress review</H>
            <span className="text-xs text-zinc-500">{fmtDateTime(latest.submitted_at)}{latest.typical_week !== "yes" ? " · non-typical week" : ""}</span>
            <Button size="sm" variant="link" className="h-auto p-0" onClick={() => setOpenSid(latest.id)} data-testid="checkin-latest-open">Answers & review</Button>
          </div>
          <div className="grid gap-3 md:grid-cols-2">{latest.domains.map((d) => <StatusCard key={d.code} card={{ ...d, overridden: !!d.overridden_by }} />)}</div>
        </div>
      )}
      {data.submissions.length > 0 && (
        <div className="space-y-2"><H>12-month trend</H><TrendGrid submissions={data.submissions} programChanges={data.program_changes} onOpen={setOpenSid} /></div>
      )}
      {enrolOpen && <EnrolDialog email={email} clientName={clientName} canonicalId={canonicalId} templates={meta.templates} staff={meta.staff} me={user} wording={meta.wording} onClose={() => setEnrolOpen(false)} onDone={load} />}
      {action && <EnrolmentActionDialog enrolment={action.en} action={action.k} staff={meta.staff} wording={meta.wording} onClose={() => setAction(null)} onDone={load} />}
      {openSid && <SubmissionDialog sid={openSid} staff={meta.staff} onClose={() => setOpenSid(null)} onChanged={load} />}
    </div>
  );
}
